// src/hooks/useFetch.ts
import { useCallback } from 'react';
import { clearClientAuthState } from '../services/authSession';

// Flag para evitar múltiples refreshes simultáneos
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export type ServiceResponse = {
  status?: number;
  message?: string;
  data?: any;
  http_status?: number;
  [k: string]: any;
};

export type ServiceParams<T = any> = {
  url: string;
  body?: T;
  headers?: Record<string, string>;
  requestType?: 'json' | 'form';
  setFetching?: (v: boolean) => void;
  onSuccess?: (r: ServiceResponse) => void;
  onError?: (r: ServiceResponse) => void;
  signal?: AbortSignal;
};

const defaultHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
});

const parseJsonSafe = async (r: Response) => {
  const text = await r.text().catch(() => '');
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text;
  }
};

const normalizeErrorMessage = (value: any): string => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(normalizeErrorMessage).filter(Boolean).join(' ');
  if (value && typeof value === 'object') {
    const first = Object.values(value)
      .map(normalizeErrorMessage)
      .find(Boolean);
    return first || 'Error en la petición';
  }
  return 'Error en la petición';
};

const getRefreshUrl = () => {
  const HOST = process.env.REACT_APP_API_HOST || 'http://localhost:8000';
  return `${HOST}/api/v1/auth/token/refresh/`;
};

/** Renueva la cookie de access vía refresh HttpOnly (credentials). */
const refreshSession = async (): Promise<boolean> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshResponse = await fetch(getRefreshUrl(), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({}),
      });
      return refreshResponse.ok;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

const redirectToLogin = () => {
  clearClientAuthState();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const callFetch = async (method: string, params: ServiceParams): Promise<ServiceResponse | void> => {
  const { url, body, requestType = 'json', setFetching, onSuccess, onError, headers, signal } = params;

  if (setFetching) setFetching(true);

  try {
    const mergedHeaders: Record<string, string> = { ...defaultHeaders(), ...(headers || {}) };

    if (body && requestType === 'json') {
      mergedHeaders['Content-Type'] = mergedHeaders['Content-Type'] ?? 'application/json; charset=utf-8';
    }

    const init: RequestInit = {
      method,
      headers: mergedHeaders,
      signal,
      credentials: 'include',
    };

    if (body) {
      init.body =
        requestType === 'json'
          ? JSON.stringify(body)
          : (() => {
              const fd = new FormData();
              Object.entries(body as any).forEach(([k, v]) => fd.append(k, v as any));
              return fd;
            })();
    }

    const res = await fetch(url, init);
    const data = await parseJsonSafe(res);
    const payload: ServiceResponse = { ...(data || {}), http_status: res.status };

    // Access expirado: renovar cookie y reintentar una vez (salvo rutas auth).
    if (res.status === 401 && window.location.pathname !== '/login' && !url.includes('/auth/')) {
      try {
        const ok = await refreshSession();
        if (ok) {
          const retryRes = await fetch(url, init);
          const retryData = await parseJsonSafe(retryRes);
          const retryPayload: ServiceResponse = { ...(retryData || {}), http_status: retryRes.status };

          if (retryRes.status === 401) {
            redirectToLogin();
            return;
          }

          if (retryRes.status === 403) {
            const permissionMessage =
              (typeof retryPayload?.message === 'string' && retryPayload.message.trim()) ||
              'No tienes permisos para realizar esta acción.';
            const permissionPayload: ServiceResponse = {
              ...retryPayload,
              http_status: 403,
              success: false,
              is_permission_denied: true,
              message: permissionMessage,
            };
            onError?.(permissionPayload);
            return permissionPayload;
          }

          if (!retryRes.ok || retryPayload.success === false) {
            const normalizedMessage = normalizeErrorMessage(
              retryPayload.errors || retryPayload.message || retryPayload.detail
            );
            onError?.({ ...retryPayload, message: normalizedMessage });
          } else {
            onSuccess?.(retryPayload);
          }
          return retryPayload;
        }
      } catch (refreshError) {
        console.error('Error al refrescar sesión:', refreshError);
      }
      redirectToLogin();
      return;
    }

    // 403 = sin permiso (sesión válida). Mostrar error en pantalla, sin redirigir.
    if (res.status === 403) {
      const permissionMessage =
        (typeof payload?.message === 'string' && payload.message.trim()) ||
        (typeof payload?.detail === 'string' && payload.detail.trim()
          ? payload.detail.includes('permission')
            ? 'No tienes permisos para realizar esta acción.'
            : payload.detail
          : '') ||
        'No tienes permisos para realizar esta acción.';

      const permissionPayload: ServiceResponse = {
        ...payload,
        http_status: 403,
        success: false,
        is_permission_denied: true,
        message: permissionMessage,
      };

      onError?.(permissionPayload);
      return permissionPayload;
    }

    if (!res.ok || payload.success === false) {
      const normalizedMessage = normalizeErrorMessage(
        payload.errors || payload.message || payload.detail
      );
      onError?.({
        ...payload,
        message: normalizedMessage,
      });
    } else {
      onSuccess?.(payload);
    }

    return payload;
  } catch (err: any) {
    if (err?.name === 'AbortError') return;
    onError?.({ message: err?.message ?? 'Network error' });
    console.error('fetch error', err);
  } finally {
    if (setFetching) setFetching(false);
  }
};

/**
 * useFetch: retorna funciones memoizadas para evitar que su identidad cambie en cada render.
 */
const useFetch = () => {
  const doGet = useCallback((p: ServiceParams) => callFetch('GET', p), []);
  const doPost = useCallback((p: ServiceParams) => callFetch('POST', p), []);
  const doPut = useCallback((p: ServiceParams) => callFetch('PUT', p), []);
  const doDelete = useCallback((p: ServiceParams) => callFetch('DELETE', p), []);

  return { doGet, doPost, doPut, doDelete };
};

export { refreshSession };
export default useFetch;
